// UI overhaul finale: the theme mechanism for "Focus" (dark mode).
//
// Reads 'theme' from localStorage ('light' | 'dark'), writes/removes the
// 'dark' class on <html>, and exposes a toggle. DEFAULT IS LIGHT: dark is
// explicit opt in while we vet it; no system-preference auto-switch yet.
// Deliberately does NOT reuse profiles.theme (that column belongs to the
// legacy public profile product, not the dashboard).

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { StatusBar, Style } from '@capacitor/status-bar'
import { isNativeShell } from '../lib/nativeShell'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'theme'

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
})

function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

function applyThemeClass(theme: Theme) {
  const root = document.documentElement
  if (theme === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
}

/**
 * CAP-SPIKE-5 — the native status bar follows the app's theme.
 *
 * With no StatusBar plugin iOS defaults to dark text, so in Focus (dark
 * theme) the clock, battery and signal were nearly invisible against the
 * dark header. This is driven from the SAME effect that applies the theme
 * class, so it reacts to a theme change while the app is running rather than
 * being set once at launch.
 *
 * THE Style ENUM IS INVERTED AND IS EASY TO GET BACKWARDS. It is named for
 * the BACKGROUND it sits on, not for the text:
 *   Style.Dark  = light text, for a DARK background  -> our 'dark' theme
 *   Style.Light = dark text,  for a LIGHT background -> our 'light' theme
 *
 * No-ops in a browser: isNativeShell() is false, so the web is untouched and
 * the plugin is never called. Failures are swallowed on purpose — a status
 * bar that stays the wrong colour is a cosmetic problem, and it must never
 * take the app down with it.
 */
function applyNativeStatusBar(theme: Theme) {
  if (!isNativeShell()) return
  void StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light }).catch(() => {
    // Plugin unavailable or the platform refused: leave the bar as it is.
  })
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const t = readStoredTheme()
    // Apply during first render so there is no light flash on reload
    // for opted-in users.
    applyThemeClass(t)
    return t
  })

  useEffect(() => {
    applyThemeClass(theme)
    // Runs on EVERY theme change, not only the first, which is what makes the
    // status bar track a live toggle.
    applyNativeStatusBar(theme)
  }, [theme])

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t)
    try {
      if (t === 'dark') localStorage.setItem(STORAGE_KEY, 'dark')
      else localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Storage unavailable (private mode): the in-session theme still works.
    }
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((cur) => {
      const next: Theme = cur === 'dark' ? 'light' : 'dark'
      try {
        if (next === 'dark') localStorage.setItem(STORAGE_KEY, 'dark')
        else localStorage.removeItem(STORAGE_KEY)
      } catch {
        // Non-fatal.
      }
      return next
    })
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext)
}
